import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutGrid, Box, Users, User, FileText, ClipboardList,
  Settings as SettingsIcon, PanelLeftClose, PanelLeftOpen, LogOut, Shield,
} from "lucide-react";
import { useAuth, ROLE_LABELS } from "@/lib/auth";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import mark from "@/assets/narad-mark.jpg";

function useOpenActions() {
  const { data } = useQuery({
    queryKey: ["open-actions"],
    queryFn: () => api.get("/questionnaires/mine"),
    staleTime: 60000,
  });
  const rows = data?.data || [];
  return rows.filter((r) => !r.responded && r.status !== "closed").length;
}

const GROUPS = [
  { items: [{ to: "/", label: "Overview", icon: LayoutGrid, roles: ["admin", "it_admin"], end: true },
            { to: "/", label: "My usage", icon: User, roles: ["member"], end: true }] },
  { label: "Portfolio", items: [
    { to: "/apps", label: "AI apps", icon: Box, roles: ["admin", "it_admin"] },
    { to: "/teams", label: "Teams", icon: Users, roles: ["admin", "it_admin", "team_lead"] },
    { to: "/people", label: "People", icon: User, roles: ["admin", "it_admin"] },
  ] },
  { label: "Decide", items: [
    { to: "/review", label: "Review", icon: ClipboardList, roles: ["admin", "it_admin", "team_lead", "member"], badge: "openActions" },
    { to: "/reports", label: "Reports", icon: FileText, roles: ["admin", "it_admin", "team_lead"] },
  ] },
];

const LOGO = (
  <img src={mark} alt="Narad" className="object-contain" style={{ mixBlendMode: "multiply" }} />
);

function Initials({ name = "" }) {
  const letters = name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  return (
    <span className="inline-flex items-center justify-center rounded-full shrink-0 font-semibold text-[13px]"
      style={{ width: 36, height: 36, background: "var(--brand-kawaii)", color: "var(--brand-navy)" }}>
      {letters || "?"}
    </span>
  );
}

export function Sidebar({ collapsed, onToggle }) {
  const { role, member, logout } = useAuth();
  const navigate = useNavigate();
  const openActions = useOpenActions();

  const navItem = (item) => {
    if (!item.roles.includes(role)) return null;
    const count = item.badge === "openActions" ? openActions : null;
    return (
      <NavLink
        key={item.label}
        to={item.to}
        end={item.end}
        title={collapsed ? item.label : undefined}
        data-testid={`nav-${item.label.toLowerCase().replace(/ /g, "-")}`}
        className={`flex items-center gap-3 h-11 px-3 rounded-[10px] text-[14px] transition-colors duration-150 ${collapsed ? "justify-center" : ""}`}
        style={({ isActive }) => ({
          color: isActive ? "var(--brand-navy)" : "rgba(247,243,230,0.85)",
          background: isActive ? "var(--brand-lime)" : "transparent",
          fontWeight: isActive ? 600 : 500,
        })}
      >
        <item.icon size={20} strokeWidth={1.75} className="shrink-0" />
        {!collapsed && <span className="flex-1">{item.label}</span>}
        {!collapsed && count > 0 && (
          <span className="text-[11px] font-semibold rounded-md px-1.5 py-0.5 tnum"
            style={{ background: "rgba(247,243,230,0.15)", color: "var(--brand-silky)" }}>{count}</span>
        )}
      </NavLink>
    );
  };

  return (
    <aside
      className="fixed left-0 top-0 bottom-0 flex flex-col z-20 transition-[width] duration-200"
      style={{ width: collapsed ? 72 : 248, background: "var(--brand-navy)" }}
    >
      <div className={`px-4 pt-6 pb-4 flex items-center ${collapsed ? "justify-center" : "gap-3"}`}>
        <span className="rounded-xl flex items-center justify-center shrink-0 overflow-hidden"
          style={{ width: 44, height: 44, background: "var(--brand-silky)", padding: 5 }}>
          {LOGO}
        </span>
        {!collapsed && (
          <>
            <div className="font-display text-[26px] font-extrabold flex-1" style={{ color: "var(--brand-silky)" }}>Narad</div>
            <button onClick={onToggle} data-testid="sidebar-toggle" aria-label="Collapse sidebar"
              className="w-8 h-8 inline-flex items-center justify-center rounded-md" style={{ color: "rgba(247,243,230,0.6)" }}>
              <PanelLeftClose size={18} strokeWidth={1.75} />
            </button>
          </>
        )}
      </div>
      {collapsed && (
        <button onClick={onToggle} data-testid="sidebar-toggle" aria-label="Expand sidebar"
          className="mx-auto mb-2 w-8 h-8 inline-flex items-center justify-center rounded-md" style={{ color: "rgba(247,243,230,0.6)" }}>
          <PanelLeftOpen size={18} strokeWidth={1.75} />
        </button>
      )}

      <nav className="flex-1 px-3 py-2 space-y-1 overflow-y-auto">
        {GROUPS.map((g, gi) => {
          const visible = g.items.filter((i) => i.roles.includes(role));
          if (visible.length === 0) return null;
          return (
            <div key={gi} className="space-y-1">
              {!collapsed && g.label && (
                <div className="px-3 pt-4 pb-1 text-[11px] font-semibold uppercase tracking-wide" style={{ color: "rgba(247,243,230,0.55)" }}>{g.label}</div>
              )}
              {visible.map(navItem)}
            </div>
          );
        })}
      </nav>

      <div className="px-3 pb-4 pt-2">
        {navItem({ to: "/settings", label: "Settings", icon: SettingsIcon, roles: ["admin", "it_admin"] })}
        <div className="my-3 h-px" style={{ background: "rgba(247,243,230,0.12)" }} />
        <div className={`flex items-center gap-3 px-1 ${collapsed ? "justify-center" : ""}`}>
          <Initials name={member?.name} />
          {!collapsed && (
            <>
              <div className="leading-tight flex-1 min-w-0">
                <div className="text-[14px] font-semibold truncate" style={{ color: "var(--brand-silky)" }}>{member?.name}</div>
                <div className="text-[12px] truncate" style={{ color: "rgba(247,243,230,0.65)" }}>
                  {member?.title
                    ? `${member.title} · ${(ROLE_LABELS[member?.role] || "").replace(/\s*\(.*\)/, "")}`
                    : ROLE_LABELS[member?.role]}
                </div>
              </div>
              <button onClick={() => { logout(); navigate("/login"); }} data-testid="logout-btn" aria-label="Sign out"
                className="w-8 h-8 inline-flex items-center justify-center rounded-md" style={{ color: "rgba(247,243,230,0.65)" }}>
                <LogOut size={18} strokeWidth={1.75} />
              </button>
            </>
          )}
        </div>
        {!collapsed && (
          <div className="flex items-start gap-2 px-1 mt-3 text-[12px] leading-snug" style={{ color: "rgba(247,243,230,0.6)" }}>
            <Shield size={14} strokeWidth={1.75} className="shrink-0 mt-0.5" />
            <span>Usage informs tool and training decisions only, never performance reviews.</span>
          </div>
        )}
      </div>
    </aside>
  );
}
